import React, {useEffect, useRef, useState, useMemo} from "react";
import {useHistory, useLocation} from "react-router-dom";
// nodejs library that concatenates classes
import classnames from "classnames";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Container,
  Row,
  Col,
} from "reactstrap";
// core components
import AuthHeader from "components/Headers/AuthHeader.js";

import NotifyComponent, { showNotify } from "components/notifications/notify";

import companyLogo from "../../../assets/img/custom/company/logo2.png";
import mv3Logo from "../../../assets/img/brand/LogoMV3.png";

// Custom styles imports
import {ImageLogo,
  Imagefigure,
  MV3Figure,
  MV3img
} from "../components/custom/login";

import LoaderBox from "../components/custom/loader/loaderBox";
import useAuth from "hooks/useAuth";
import { useFormHandler } from "hooks";
import { describeApiError } from "services/apiErrorMessage";

function Login() {
  const history = useHistory();
  const notifyRef = useRef(null);
  const location = useLocation();
  const authError = location.state?.authError;
  const { signIn: performLogin, isAuthenticated } = useAuth();

  const [loader, setLoader] = useState(false);

  // O admin encerra a sessão em 401/403 e manda o motivo por aqui, no lugar
  // do antigo window.alert que travava a aba (precatorios_web#13).
  useEffect(() => {
    if (authError) {
      showNotify(notifyRef, "Sessão encerrada", authError, "warning");
    }
  }, [authError]);

  const validate = useMemo(
    () => (values) => {
      const errors = {};
      if (!values.username?.trim()) {
        errors.username = "Informe o usuário";
      }
      if (!values.password?.trim()) {
        errors.password = "Informe a senha";
      }
      return errors;
    },
    [],
  );

  const form = useFormHandler(
    {
      username: "",
      password: "",
    },
    {
      validate,
      onError: (validationErrors) => {
        const message =
          validationErrors.username ||
          validationErrors.password ||
          "Por favor, verifique os campos obrigatórios.";
        showNotify(notifyRef, "Erro", message, "danger");
      },
    },
  );

  const loginExecute = form.handleSubmit(async (values) => {
    setLoader(true);
    try {
      await performLogin(values);
      setLoader(false);
      history.push("/admin/home");
    } catch (error) {
      // Sem resposta do servidor isto mostrava "Undefined error"
      // (incidente de 28/09/2026, precatorios_web#9).
      showNotify(notifyRef, "Erro", describeApiError(error), "danger");
      setLoader(false);
    }
    });

  const usernameField = form.register("username");
  const passwordField = form.register("password");

  const [focusedEmail, setfocusedEmail] = React.useState(false);
  const setFocusedEmailTrue = () => setfocusedEmail(true);
  const setFocusedEmailFalse = () => setfocusedEmail(false);

  const [focusedPassword, setfocusedPassword] = React.useState(false);
  const setFocusedPasswordTrue = () => setfocusedPassword(true);
  const setFocusedPasswordFalse = () => setfocusedPassword(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      history.push("/admin/home");
    }
  }, [history, isAuthenticated]);
  return (
    <>
      <AuthHeader/>
      <NotifyComponent notifyRef={notifyRef}/>
      <Container className="mt--9 pb-5">
        {loader ? (
          <LoaderBox/>
        ) : (
          <Row className="justify-content-center">
            <Col lg="5" md="7">
              <Card className="bg-secondary border-0 mb-0">
                <CardHeader className="bg-transparent" style={{
                  paddingBottom: '0px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Imagefigure>
                    <ImageLogo src={companyLogo}/>
                  </Imagefigure>
                </CardHeader>
                <CardBody className="px-lg-5 py-lg-4" style={{borderBottom: "0.5px solid #DFDFDF"}}>
                  <div className="text-center text-muted mb-4">
                    <small>Entre com suas credenciais</small>
                  </div>
                  <Form role="form" onSubmit={loginExecute}>
                    <FormGroup
                      className={classnames("mb-3", {
                        focused: focusedEmail,
                        })}
                    >
                      <InputGroup className="input-group-merge input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-email-83" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Usuário"
                          type="text"
                          id="username"
                          {...usernameField}
                          onFocus={setFocusedEmailTrue}
                          onBlur={(event) => {
                            setFocusedEmailFalse();
                            usernameField.onBlur?.(event);
                          }}
                          invalid={form.touched.username && Boolean(form.errors.username)}
                        />
                      </InputGroup>
                      {form.touched.username && form.errors.username ? (
                        <small className="text-danger d-block mt-2">
                          {form.errors.username}
                        </small>
                      ) : null}
                    </FormGroup>
                    <FormGroup
                      className={classnames({
                        focused: focusedPassword,
                      })}
                    >
                      <InputGroup className="input-group-merge input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-lock-circle-open" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Senha"
                          type="password"
                          id="password"
                          {...passwordField}
                          onFocus={setFocusedPasswordTrue}
                          onBlur={(event) => {
                            setFocusedPasswordFalse();
                            passwordField.onBlur?.(event);
                          }}
                          invalid={form.touched.password && Boolean(form.errors.password)}
                        />
                      </InputGroup>
                      {form.touched.password && form.errors.password ? (
                        <small className="text-danger d-block mt-2">
                          {form.errors.password}
                        </small>
                      ) : null}
                    </FormGroup>
                    <div className="text-center">
                      <Button className="my-4" color="primary" type="submit">
                        Entrar
                      </Button>
                    </div>
                  </Form>
                </CardBody>
                <CardHeader className="bg-transparent" style={{
                  paddingBottom: '0px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <MV3Figure>
                    Developed By &nbsp;
                    <MV3img src={mv3Logo}/>
                  </MV3Figure>
                </CardHeader>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
}

export default Login;
